import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
import Layout from "@/components/Layout";
import { api } from "@/lib/api";
import { fmtINR, fmtDate } from "@/lib/leadUtils";
import { PROJECT_STATUSES, PROJECT_STATUS_COLORS, daysToGo } from "@/lib/projectUtils";

const STATUS_FILTERS = ["All", ...PROJECT_STATUSES.filter((s) => s !== "Completed" && s !== "Cancelled"), "Completed", "Cancelled"];
const DENSITY_KEY = "ls.projects.density";  // localStorage — mirrors Pipeline's pattern
const DENSITY_OPTS = [
  { v: "spacious", l: "Spacious" },
  { v: "auto", l: "Auto" },
  { v: "compact", l: "Compact" },
];
const COMPACT_THRESHOLD = 8;  // switch Auto -> Compact once we exceed this row count

export default function Projects() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [showCancelled, setShowCancelled] = useState(false);
  const [density, setDensity] = useState(() => {
    try { return localStorage.getItem(DENSITY_KEY) || "auto"; } catch { return "auto"; }
  });
  const navigate = useNavigate();

  useEffect(() => {
    try { localStorage.setItem(DENSITY_KEY, density); } catch {}
  }, [density]);

  const refresh = async () => {
    setLoading(true);
    const params = {};
    if (q.trim()) params.q = q.trim();
    if (statusFilter !== "All" && statusFilter !== "Completed" && statusFilter !== "Cancelled") params.status = statusFilter;
    if (statusFilter === "Completed") { params.status = "Completed"; params.include_completed = true; }
    if (statusFilter === "Cancelled") { params.status = "Cancelled"; params.include_cancelled = true; }
    if (statusFilter === "All") { params.include_completed = false; params.include_cancelled = showCancelled; }
    const { data } = await api.get("/projects", { params });
    setItems(data.items || []);
    setLoading(false);
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [q, statusFilter, showCancelled]);

  const useCompact = useMemo(() => {
    if (density === "compact") return true;
    if (density === "spacious") return false;
    return items.length > COMPACT_THRESHOLD;  // auto
  }, [density, items.length]);

  return (
    <Layout>
      <div className="fade-up" data-testid="projects-page">
        <div className="flex items-end justify-between gap-4 mb-8 flex-wrap">
          <div>
            <div className="kicker mb-3" style={{ color: "var(--ds-accent)" }}>PRODUCTION</div>
            <h1
              data-testid="projects-title"
              style={{
                fontFamily: "var(--ds-num-font)",
                fontSize: "clamp(40px, 5.6vw, 62px)",
                color: "var(--ds-ink)",
                letterSpacing: "-0.01em",
                lineHeight: 1,
                fontWeight: 400,
              }}
            >
              Projects
            </h1>
            <div className="text-sm mt-2" style={{ color: "var(--ds-text-2)" }} data-testid="projects-count">{items.length} projects</div>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            {/* Density segmented toggle (matches Pipeline pattern) */}
            <div
              data-testid="density-switcher"
              className="flex"
              style={{
                background: "#FFF",
                border: "1px solid #E9E2D6",
                borderRadius: 8,
                padding: 4,
              }}
            >
              {DENSITY_OPTS.map((o) => (
                <button
                  key={o.v}
                  onClick={() => setDensity(o.v)}
                  data-testid={`density-${o.v}`}
                  style={{
                    padding: "9px 18px",
                    borderRadius: 5,
                    fontSize: 11,
                    fontWeight: 600,
                    letterSpacing: "0.13em",
                    textTransform: "uppercase",
                    background: density === o.v ? "var(--ds-ink)" : "transparent",
                    color: density === o.v ? "#F4EFE4" : "#857E6F",
                    transition: "all .18s ease",
                    border: "none",
                    cursor: "pointer",
                  }}
                >
                  {o.l}
                </button>
              ))}
            </div>
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--ds-muted)" }} />
              <input
                data-testid="projects-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search client"
                className="outline-none"
                style={{
                  background: "#FCFAF5",
                  border: "1px solid #E4DCCD",
                  borderRadius: 8,
                  padding: "9px 12px 9px 34px",
                  fontSize: 13,
                  width: 256,
                  color: "var(--ds-text)",
                  fontFamily: "var(--ds-body-font)",
                  transition: "border-color .15s, box-shadow .15s",
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = "#B08D55";
                  e.currentTarget.style.boxShadow = "0 0 0 3px rgba(176,141,85,.12)";
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = "#E4DCCD";
                  e.currentTarget.style.boxShadow = "none";
                }}
              />
            </div>
          </div>
        </div>

        {/* Status filter tab bar — white container with bottom-underline active state (spec) */}
        <div
          data-testid="status-filter"
          style={{
            background: "#FFF",
            border: "1px solid #E9E2D6",
            borderRadius: 12,
            padding: "0 14px",
            marginBottom: 24,
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 4,
          }}
        >
          {STATUS_FILTERS.map((s) => {
            const isActive = statusFilter === s;
            return (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                data-testid={`status-pill-${s.toLowerCase().replace(/\s+/g, "-")}`}
                style={{
                  padding: "14px 16px 12px",
                  borderBottom: `2.5px solid ${isActive ? "var(--ds-ink)" : "transparent"}`,
                  background: "transparent",
                  color: isActive ? "var(--ds-ink)" : "#857E6F",
                  fontSize: 11,
                  fontWeight: isActive ? 700 : 600,
                  letterSpacing: "0.16em",
                  textTransform: "uppercase",
                  cursor: "pointer",
                  transition: "color .15s, border-color .15s",
                  marginBottom: -1,
                }}
              >
                {s}
              </button>
            );
          })}
          {statusFilter === "All" && (
            <label
              className="ml-auto flex items-center gap-2 cursor-pointer"
              style={{ fontSize: 11, letterSpacing: "0.16em", textTransform: "uppercase", color: "var(--ds-text-2)", padding: "12px 4px" }}
              data-testid="projects-show-cancelled-label"
            >
              <input type="checkbox" checked={showCancelled} onChange={(e) => setShowCancelled(e.target.checked)} data-testid="projects-show-cancelled" />
              Show cancelled
            </label>
          )}
        </div>

        {loading ? (
          <div className="kicker" data-testid="projects-loading">Loading</div>
        ) : items.length === 0 ? (
          <div
            data-testid="projects-empty"
            className="text-center"
            style={{
              background: "#FFF",
              border: "1px solid #E9E2D6",
              borderRadius: 12,
              boxShadow: "0 2px 6px rgba(60,46,20,.05)",
              padding: 48,
            }}
          >
            <div className="kicker mb-3">NOTHING IN PRODUCTION YET</div>
            <p className="text-sm" style={{ color: "var(--ds-text-2)" }}>When a lead is moved to <span className="font-medium">Onboarded</span>, the project lands here.</p>
          </div>
        ) : (
          <div className={useCompact ? "" : ""} data-testid="projects-list" data-density={useCompact ? "compact" : "spacious"} style={{ display: "flex", flexDirection: "column", gap: useCompact ? 6 : 12 }}>
            {items.map((p) => (
              useCompact
                ? <ProjectRowCompact key={p.id} p={p} onClick={() => navigate(`/projects/${p.id}`)} />
                : <ProjectCard key={p.id} p={p} onClick={() => navigate(`/projects/${p.id}`)} />
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}

function ProjectCard({ p, onClick }) {
  const dtg = daysToGo(p.wedding_date);
  const upcoming = dtg !== null && dtg >= 0;
  const past = dtg !== null && dtg < 0;
  const badge =
    dtg === null ? null : upcoming ? `${dtg} day${dtg === 1 ? "" : "s"} to go` : `${Math.abs(dtg)} day${Math.abs(dtg) === 1 ? "" : "s"} past`;
  const badgeBg = upcoming ? "var(--ds-pos-bg)" : past ? "var(--ds-neg-bg)" : "#F1EDE2";
  const badgeFg = upcoming ? "var(--ds-pos)" : past ? "var(--ds-neg)" : "var(--ds-text-2)";

  return (
    <div
      onClick={onClick}
      data-testid={`project-card-${p.id}`}
      style={{
        background: "#FFF",
        border: "1px solid #EDE5D8",
        borderRadius: 12,
        padding: 22,
        boxShadow: "0 2px 6px rgba(60,46,20,.05)",
        cursor: "pointer",
        transition: "box-shadow .15s ease, transform .15s ease, border-color .15s ease",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = "0 6px 16px rgba(60,46,20,.12)";
        e.currentTarget.style.borderColor = "#D8CCBB";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = "0 2px 6px rgba(60,46,20,.05)";
        e.currentTarget.style.borderColor = "#EDE5D8";
      }}
    >
      <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_0.8fr_1.4fr_1fr] gap-6 items-center">
        <div>
          <div
            style={{
              fontFamily: "var(--ds-num-font)",
              fontSize: 18,
              color: "var(--ds-ink)",
              letterSpacing: "-0.01em",
              lineHeight: 1.2,
            }}
            data-testid={`project-card-client-${p.id}`}
          >
            {p.client_name}
          </div>
          <div className="text-sm mt-1.5" style={{ color: "var(--ds-text-2)" }}>
            Wedding · {fmtDate(p.wedding_date)}
            {badge && (
              <span
                className="ml-2 inline-block"
                style={{
                  background: badgeBg,
                  color: badgeFg,
                  padding: "3px 9px",
                  fontSize: 10,
                  fontWeight: 600,
                  letterSpacing: "0.14em",
                  textTransform: "uppercase",
                  borderRadius: 5,
                }}
              >
                {badge}
              </span>
            )}
          </div>
        </div>

        <div data-testid={`project-card-status-${p.id}`}>
          <div className="kicker mb-1.5">STATUS</div>
          <span
            className="inline-block"
            style={{
              background: PROJECT_STATUS_COLORS[p.status] || "var(--ds-text-2)",
              color: "#fff",
              padding: "6px 12px",
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              borderRadius: 7,
            }}
          >
            {p.status}
          </span>
        </div>

        <div className="grid grid-cols-3 gap-4">
          <Marker label="Services" value={`${p.services_assigned} of ${p.services_total}`} />
          <Marker label="Confirmed" value={`${p.vendors_confirmed} of ${p.vendors_total}`} />
          <Marker label="Deliverables" value={String(p.deliverables_pending)} />
        </div>

        <div className="text-right">
          <div className="kicker mb-1.5">MARGIN</div>
          <div
            style={{
              fontSize: 15,
              fontWeight: 600,
              fontVariantNumeric: "tabular-nums",
              color: p.margin < 0 ? "var(--ds-neg)" : "var(--ds-ink)",
            }}
          >
            {fmtINR(p.margin)}
          </div>
        </div>
      </div>
    </div>
  );
}

function ProjectRowCompact({ p, onClick }) {
  // Single-line table-ish row for compact density. Visual aligned with the
  // Phase 7.0 floating-pill aesthetic but with smaller dimensions.
  const dtg = daysToGo(p.wedding_date);
  const upcoming = dtg !== null && dtg >= 0;
  const past = dtg !== null && dtg < 0;
  const badge = dtg === null ? null : upcoming ? `${dtg}d` : `-${Math.abs(dtg)}d`;
  const badgeFg = upcoming ? "var(--ds-pos)" : past ? "var(--ds-neg)" : "var(--ds-text-2)";
  return (
    <div
      onClick={onClick}
      data-testid={`project-row-${p.id}`}
      className="grid grid-cols-[1.6fr_0.9fr_0.7fr_1.2fr_1fr] gap-3 items-center text-sm cursor-pointer"
      style={{
        background: "#FFF",
        border: "1px solid #EDE5D8",
        borderRadius: 10,
        padding: "12px 16px",
        boxShadow: "0 1px 3px rgba(60,46,20,.04)",
        transition: "border-color .15s, box-shadow .15s",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = "#D8CCBB";
        e.currentTarget.style.boxShadow = "0 3px 8px rgba(60,46,20,.08)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = "#EDE5D8";
        e.currentTarget.style.boxShadow = "0 1px 3px rgba(60,46,20,.04)";
      }}
    >
      <div className="truncate" data-testid={`project-row-client-${p.id}`}>
        <span style={{ color: "var(--ds-ink)", fontWeight: 600 }}>{p.client_name}</span>
      </div>
      <div className="text-xs" style={{ color: "var(--ds-text-2)" }}>
        {fmtDate(p.wedding_date)}{badge && <span className="ml-2" style={{ color: badgeFg, fontWeight: 600 }}>{badge}</span>}
      </div>
      <div>
        <span
          className="inline-block"
          style={{
            background: PROJECT_STATUS_COLORS[p.status] || "var(--ds-text-2)",
            color: "#fff",
            padding: "4px 9px",
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: "0.1em",
            textTransform: "uppercase",
            borderRadius: 6,
          }}
        >
          {p.status}
        </span>
      </div>
      <div className="text-xs" style={{ color: "var(--ds-text-2)" }}>
        Svc {p.services_assigned}/{p.services_total} · Conf {p.vendors_confirmed}/{p.vendors_total}
      </div>
      <div
        className="text-right text-xs"
        style={{
          color: p.margin < 0 ? "var(--ds-neg)" : "var(--ds-ink)",
          fontWeight: 600,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {fmtINR(p.margin)}
      </div>
    </div>
  );
}

function Marker({ label, value }) {
  return (
    <div>
      <div className="kicker mb-1">{label}</div>
      <div className="text-sm" style={{ color: "var(--ds-ink)", fontWeight: 500 }}>{value}</div>
    </div>
  );
}
